import React, { useMemo } from 'react';
import {
  PIXEL_PERSON_GRID_WIDTH,
  PIXEL_PERSON_VIEWBOX_PAD_TOP,
  PIXEL_PERSON_VIEWBOX_HEIGHT,
  buildPixelPersonGrid,
} from './pixelPersonGrid';
import {
  PIXEL_PERSON_PARTS,
  PIXEL_PERSON_PART_ORIGINS,
  resolvePixelPersonPart,
} from './pixelPersonParts';
import { resolvePixelPersonColors, resolvePixelPersonHairStyleId, resolvePixelPersonHeadwearId, resolvePixelPersonSeed, MASTER_AGENT_HEADWEAR_ID, SYSTEM_AGENT_HEADWEAR_ID } from './pixelPersonUtils';
import MasterAgentMatrixRain from './MasterAgentMatrixRain';
import './PixelPerson.css';

function zoneColor(zone, colors) {
  switch (zone) {
    case 'hair':
      return colors.hair;
    case 'skin':
      return colors.skin;
    case 'eye':
      return colors.eye;
    case 'shirt':
      return colors.shirt;
    case 'pants':
      return colors.pants;
    case 'shoes':
      return colors.shoes;
    case 'headwear':
      return colors.headwear;
    case 'headwearAccent':
      return colors.headwearAccent;
    case 'outline':
      return colors.outline;
    default:
      return null;
  }
}

function PixelRect({ rowIdx, colIdx, fill }) {
  return <rect x={colIdx} y={rowIdx} width={1} height={1} fill={fill} />;
}

function PixelPartGroup({ part, rects, children, className = '' }) {
  if (rects.length === 0 && !children) {
    return null;
  }

  return (
    <g
      className={['pixel-person__part', `pixel-person__part--${part}`, className]
        .filter(Boolean)
        .join(' ')}
      style={{ transformOrigin: PIXEL_PERSON_PART_ORIGINS[part] }}
    >
      {rects.map(({ key, rowIdx, colIdx, fill }) => (
        <PixelRect key={key} rowIdx={rowIdx} colIdx={colIdx} fill={fill} />
      ))}
      {children}
    </g>
  );
}

/**
 * Inline SVG pixel person with layered body-part animation on hover.
 */
export default function PixelPerson({
  name,
  size = 32,
  hair,
  hairStyle,
  headwear,
  shirt,
  pants,
  shoes,
  tone,
  seed,
  systemAgent = false,
  masterAgent = false,
  animated = true,
  className = '',
  onClick,
  ...rest
}) {
  const resolvedSeed = useMemo(
    () => resolvePixelPersonSeed({ name, seed }),
    [name, seed]
  );
  const resolvedHairStyleId = useMemo(
    () => resolvePixelPersonHairStyleId(resolvedSeed, hairStyle),
    [resolvedSeed, hairStyle]
  );
  const resolvedHeadwearId = useMemo(() => {
    if (masterAgent && headwear === MASTER_AGENT_HEADWEAR_ID) {
      return MASTER_AGENT_HEADWEAR_ID;
    }
    if (systemAgent && headwear === SYSTEM_AGENT_HEADWEAR_ID) {
      return SYSTEM_AGENT_HEADWEAR_ID;
    }
    return resolvePixelPersonHeadwearId(resolvedSeed, headwear);
  }, [resolvedSeed, headwear, systemAgent, masterAgent]);
  const colors = useMemo(
    () => resolvePixelPersonColors({ hair, shirt, pants, shoes, tone }, resolvedSeed, resolvedHeadwearId),
    [hair, shirt, pants, shoes, tone, resolvedSeed, resolvedHeadwearId]
  );

  const framePadding = Math.max(3, Math.round(size * 0.08));
  const svgHeight = Math.max(12, size - framePadding * 2);
  const width = Math.round((svgHeight / PIXEL_PERSON_VIEWBOX_HEIGHT) * PIXEL_PERSON_GRID_WIDTH);
  const viewBox = `0 0 ${PIXEL_PERSON_GRID_WIDTH} ${PIXEL_PERSON_VIEWBOX_HEIGHT}`;
  const contentOffset = PIXEL_PERSON_VIEWBOX_PAD_TOP;

  const partRects = useMemo(() => {
    const grouped = Object.fromEntries(PIXEL_PERSON_PARTS.map((part) => [part, []]));
    const grid = buildPixelPersonGrid(resolvedHairStyleId, resolvedHeadwearId);

    grid.forEach((row, rowIdx) => {
      row.forEach((zone, colIdx) => {
        const fill = zoneColor(zone, colors);
        const part = resolvePixelPersonPart(rowIdx, colIdx, zone);
        if (!fill || !part) {
          return;
        }
        grouped[part].push({ rowIdx, colIdx, fill, key: `${part}-${rowIdx}-${colIdx}` });
      });
    });

    return grouped;
  }, [colors, resolvedHairStyleId, resolvedHeadwearId]);

  const wrapClassName = [
    'pixel-person-wrap',
    animated ? 'pixel-person-wrap--animated' : '',
    systemAgent ? 'pixel-person-wrap--system' : '',
    masterAgent ? 'pixel-person-wrap--master' : '',
    onClick ? 'pixel-person-wrap--clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={wrapClassName}
      style={{
        '--pixel-person-accent': colors.shirt,
        '--pixel-person-width': `${width}px`,
        '--pixel-person-height': `${size}px`,
        '--pixel-person-chip-size': `${size}px`,
      }}
      onClick={onClick}
      {...rest}
    >
      {masterAgent ? (
        <MasterAgentMatrixRain seed={resolvedSeed} size={size} />
      ) : null}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="pixel-person pixel-person__sprite"
        width={width}
        height={svgHeight}
        viewBox={viewBox}
        overflow="visible"
        shapeRendering="crispEdges"
        aria-hidden={rest['aria-label'] ? undefined : true}
      >
        <g transform={`translate(0 ${contentOffset})`}>
          <ellipse
            className="pixel-person__shadow"
            cx={7}
            cy={17.2}
            rx={3.2}
            ry={0.55}
          />
          <g className="pixel-person__figure">
            <PixelPartGroup part="legLeft" rects={partRects.legLeft} />
            <PixelPartGroup part="legRight" rects={partRects.legRight} />
            <PixelPartGroup part="torso" rects={partRects.torso} />
            <PixelPartGroup part="armLeft" rects={partRects.armLeft} />
            <PixelPartGroup part="armRight" rects={partRects.armRight} />
            <PixelPartGroup part="head" rects={partRects.head} />
            <PixelPartGroup part="headwear" rects={partRects.headwear} />
            <PixelPartGroup part="eyes" rects={partRects.eyes} />
          </g>
        </g>
      </svg>
    </div>
  );
}
